import React from "react"

import type { CompanyConnectorDto } from "~/ui-rtk/api/types/company-connector.types"
import {
  isMetaDataFallbackConnector,
  metaSystemUserNameOf,
} from "~/ui-rtk/utils/connector-utils"

type MetaCredentialSectionProps = {
  connector: CompanyConnectorDto
}

/**
 * Super-admin "Meta credential" line in the Instagram Insights details dialog,
 * for a connector that runs on a Meta system user: which one, and for a data
 * fallback (APP-548) since when. Read-only: a system user's token is set on
 * Setup → Connectors → Meta Advanced.
 */
const MetaCredentialSection: React.FC<MetaCredentialSectionProps> = ({
  connector,
}) => {
  const name = metaSystemUserNameOf(connector.metadata) ?? "unnamed"
  const isDataFallback = isMetaDataFallbackConnector(connector.metadata)
  const fallbackSince = (connector.metadata as { dataFallbackSince?: unknown })
    ?.dataFallbackSince
  const since =
    typeof fallbackSince === "string"
      ? ` since ${fallbackSince.slice(0, 10)}`
      : ""

  return (
    <section
      aria-label="Meta credential"
      data-testid="meta-credential-section"
      className="flex flex-col gap-2 text-left mb-4 p-3 bg-gray-700/50 border border-gray-600 rounded-lg"
    >
      <p className="text-4 leading-7 font-bold text-platinum">
        Meta credential
      </p>
      <p className="text-sm text-gray-300">
        {`System user ${name}`}
        {isDataFallback &&
          ` · Data fallback for a login that failed (Meta Advanced)${since}. The brand's working login takes it back.`}
      </p>
      {isDataFallback && (
        <p className="text-sm text-amber-300">
          To rotate the token, replace the Meta Advanced credential.
        </p>
      )}
    </section>
  )
}

export default MetaCredentialSection
